import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Menu from "@/components/@common/Menu";
import Icon from "@/components/icon/Icon";
import { FoldingArrowDown, Sizes } from "@/components/icon/IconList";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { Icon as IconV2, Typography } from "@bookips/sds";
import { useEffect, useRef, useState } from "react";
import { purchasableSubjectsList } from "src/api/product";
/**
 * GNB 검색 입력 공통 컴포넌트
 * 과목 셀렉트박스 + 검색 입력창 + clear/submit 버튼
 */ const GNBSearchInput = (param)=>{
    let { value , onChange , onClear , onSubmit , selectedSubject , onSubjectChange , placeholder ="찾고 싶은 자료를 검색해보세요" , isActive =true , onInputClick , autoFocus =false , className  } = param;
    var _subjects_find;
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const selectRef = useRef(null);
    const inputRef = useRef(null);
    const subjects = purchasableSubjectsList();
    const selectedSubjectLabel = ((_subjects_find = subjects.find((s)=>s.key === selectedSubject)) === null || _subjects_find === void 0 ? void 0 : _subjects_find.label) || subjects[0].label;
    // 외부 클릭 감지 - Select 드롭다운
    useEffect(()=>{
        const handleClickOutside = (event)=>{
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsSelectOpen(false);
            }
        };
        if (isSelectOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return ()=>{
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [
        isSelectOpen
    ]);
    // 자동 포커스
    useEffect(()=>{
        if (autoFocus) {
            var _inputRef_current;
            (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
        }
    }, [
        autoFocus
    ]);
    const handleSelectClick = ()=>{
        setIsSelectOpen(!isSelectOpen);
    };
    const handleSubjectChange = (newSubject)=>{
        var _subjects_find;
        onSubjectChange(newSubject);
        setIsSelectOpen(false);
        // MX-336: 검색 핸들러 과목 선택 트래킹
        const subjectLabel = (_subjects_find = subjects.find((s)=>s.key === newSubject)) === null || _subjects_find === void 0 ? void 0 : _subjects_find.label;
        mixpanelEvent("Search Handler Clicked", {
            "Handler Type": subjectLabel
        });
    };
    const handleSubmit = (e)=>{
        e.preventDefault();
        onSubmit();
    };
    const handleInputClick = ()=>{
        // 이미 활성 상태면 열기 요청을 다시 보내지 않는다
        // (재호출 시 검색어가 URL 값으로 되돌아가고 트래킹이 중복 발사됨)
        if (!isActive) {
            onInputClick === null || onInputClick === void 0 ? void 0 : onInputClick();
        }
        // 다음 렌더링 프레임 이후 input에 포커스
        requestAnimationFrame(()=>{
            var _inputRef_current;
            (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
        });
    };
    return /*#__PURE__*/ _jsxs("form", {
        onSubmit: handleSubmit,
        className: cn("relative flex items-center gap-1", "h-10 rounded-full", "pr-4", isActive ? "border border-gray-700 bg-white" : "border border-transparent bg-gray-300/20", className),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                ref: selectRef,
                className: "relative flex h-full items-center",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: handleSelectClick,
                        className: cn("flex h-full items-center gap-1", "pr-2 pl-5", "focus:outline-none"),
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "subtitle2",
                                color: "primary",
                                children: selectedSubjectLabel
                            }),
                            /*#__PURE__*/ _jsx(Icon, {
                                icon: FoldingArrowDown,
                                size: Sizes.xsmall,
                                className: cn("transition-transform", isSelectOpen ? "rotate-0" : "rotate-180")
                            })
                        ]
                    }),
                    isSelectOpen && /*#__PURE__*/ _jsx("div", {
                        className: "absolute top-full left-0 z-50 mt-1",
                        children: /*#__PURE__*/ _jsx(Menu, {
                            items: subjects.map((s)=>({
                                    label: s.label,
                                    value: s.key
                                })),
                            selectedValue: selectedSubject,
                            onChange: handleSubjectChange,
                            classes: {
                                root: "w-[120px] shadow-sm border border-gray-200 rounded-md"
                            }
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex h-full flex-1 items-center gap-2 px-0 py-1",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative flex-1",
                        children: [
                            /*#__PURE__*/ _jsx("input", {
                                ref: inputRef,
                                type: "text",
                                readOnly: !isActive && !!onInputClick,
                                value: value,
                                onChange: onChange,
                                onClick: onInputClick ? handleInputClick : undefined,
                                placeholder: placeholder,
                                autoFocus: autoFocus,
                                className: cn("w-full border-none bg-transparent outline-none", "text-base leading-6 font-normal", "text-gray-900 placeholder:text-[rgba(28,31,46,0.5)]", !isActive && onInputClick && "cursor-pointer")
                            }),
                            isActive && !value && /*#__PURE__*/ _jsx("div", {
                                className: "pointer-events-none absolute top-1/2 left-0 h-5 w-[1.5px] -translate-y-1/2 animate-pulse bg-indigo-500"
                            })
                        ]
                    }),
                    isActive && value && /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: onClear,
                        className: "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-100",
                        children: /*#__PURE__*/ _jsx(IconV2.Close_Fill, {
                            className: "size-5 text-gray-700"
                        })
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "submit",
                        className: cn("flex flex-shrink-0 items-center justify-center", "h-8 w-8 rounded", "transition-colors hover:bg-gray-100/50"),
                        "aria-label": "검색",
                        children: /*#__PURE__*/ _jsx(IconV2.Search_2px, {
                            className: "size-5 text-gray-600"
                        })
                    })
                ]
            })
        ]
    });
};
export default GNBSearchInput;
